import React from 'react';
import { Search, Filter, BarChart2, Trophy, GitCompare, Inbox, Activity, DollarSign, BookOpen, FileText } from 'lucide-react';

// Slash-command shortcuts surfaced under the composer when the user
// types `/`. Each command maps to a templated prompt — the model still
// picks the right tool, but the prompt is precise enough that the
// matching tool call is the obvious next step.
export const SLASH_COMMANDS = [
  {
    cmd: '/search',
    label: 'Search brands',
    description: 'Free-text search across name, parent, notes.',
    Icon: Search,
    template: (rest) => `Search for ${rest || '...'}.`,
  },
  {
    cmd: '/filter',
    label: 'Filter brands',
    description: 'Typed filters: category, country, founded year, marketplaces…',
    Icon: Filter,
    template: (rest) => `Filter brands by ${rest || '...'}.`,
  },
  {
    cmd: '/brand',
    label: 'Inspect one brand',
    description: 'Get a single brand by name.',
    Icon: BookOpen,
    template: (rest) => `Tell me about ${rest || 'this brand'}.`,
  },
  {
    cmd: '/metrics',
    label: 'Brand metrics',
    description: "Recent social metrics + weekly per-post for one brand.",
    Icon: BarChart2,
    template: (rest) => `Show me recent metrics for ${rest || 'this brand'}.`,
  },
  {
    cmd: '/momentum',
    label: 'Momentum score',
    description: 'Latest Focus-Queue momentum score for one brand.',
    Icon: Activity,
    template: (rest) => `What's the momentum score for ${rest || 'this brand'}?`,
  },
  {
    cmd: '/rank',
    label: 'Rankings',
    description: 'Top brands by week-over-week per-post growth.',
    Icon: Trophy,
    template: (rest) => `Show me the top brands by ${rest || 'views'} this week.`,
  },
  {
    cmd: '/compare',
    label: 'Compare brands',
    description: 'Side-by-side comparison of 2–6 brands.',
    Icon: GitCompare,
    template: (rest) => `Compare ${rest || 'brand A and brand B'}.`,
  },
  {
    cmd: '/report',
    label: 'Generate report',
    description: 'Markdown report with charts, grounded in the database.',
    Icon: FileText,
    template: (rest) =>
      `Generate a report on ${rest || 'the brands matching this filter'}, ` +
      'with summary, highlights, table, and at least one chart.',
  },
  {
    cmd: '/pending',
    label: 'Review queue',
    description: 'What\'s in the pending-brand approval queue.',
    Icon: Inbox,
    template: (rest) => `What's in my review queue${rest ? ` for ${rest}` : ''}?`,
  },
  {
    cmd: '/spend',
    label: 'Credit usage',
    description: 'Recent credit spend by operation type.',
    Icon: DollarSign,
    template: (rest) => `How much have we spent on credits in the last ${rest || '30 days'}?`,
  },
];

// Shared with the composer so its keyboard navigation operates on the
// exact list being rendered.
export function filterSlashCommands(filter) {
  return SLASH_COMMANDS.filter(c =>
    !filter || c.cmd.startsWith(filter) || c.label.toLowerCase().includes(filter.slice(1).toLowerCase()),
  );
}

const STROKE = 2.75;

function SlashMenu({ items, selectedIndex, onPick, onHover }) {
  if (!items.length) return null;
  return (
    <div className="chat-slash-menu elev-lg" role="listbox">
      {items.map((c, idx) => {
        const I = c.Icon;
        const selected = idx === selectedIndex;
        return (
          <button
            key={c.cmd}
            type="button"
            className={`chat-slash-item${selected ? ' is-selected' : ''}`}
            onClick={() => onPick(c)}
            onMouseEnter={() => onHover && onHover(idx)}
            role="option"
            aria-selected={selected}
          >
            <span className="chat-slash-icon"><I size={16} strokeWidth={STROKE} /></span>
            <span className="chat-slash-text">
              <span className="chat-slash-label">
                <code>{c.cmd}</code> {c.label}
              </span>
              <span className="chat-slash-desc">{c.description}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default SlashMenu;
